import { computed, ref } from 'vue'

import { PostContentType, PostVisibility } from '@courses/utility-layer/enums'
import { defineStore } from 'pinia'

import type { EmbeddedMediaJSON, Post } from '@courses/types-layer/types/post'

/**
 * Posts UI Store - handles only UI state
 * NO server data, NO service imports, NO API calls
 */
export const usePostsUIStore = defineStore('posts-theme-ui', () => {
  const videoPlayedSoFar = ref<Array<number>>([]) // This a time in seconds
  const audioPlayedSoFar = ref<Array<number>>([]) // This a time in seconds

  const setVideoPlayedSoFar = (time: Array<number>) => {
    videoPlayedSoFar.value = time
  }

  const getVideoPlayedSoFar = () => {
    return videoPlayedSoFar.value
  }

  const getVideoCompletedPercentage = () => {
    if (videoPlayedSoFar.value.length === 0) {
      return 0
    }
    return (videoPlayedSoFar.value[0] / videoPlayedSoFar.value[1]) * 100
  }

  const setAudioPlayedSoFar = (time: Array<number>) => {
    audioPlayedSoFar.value = time
  }

  const getAudioPlayedSoFar = () => {
    return audioPlayedSoFar.value
  }

  const getAudioCompletedPercentage = () => {
    if (audioPlayedSoFar.value.length === 0) {
      return 0
    }
    return (audioPlayedSoFar.value[0] / audioPlayedSoFar.value[1]) * 100
  }

  // Computed getters for post data processing (UI logic only)
  const getPostMaterials = computed(() => (post: Post) => {
    return post.post_materials || []
  })

  const getAssetLicenseId = computed(() => (post: Post) => {
    return post.asset_urls?.licenseId || ''
  })

  const getAssetType = computed(() => (post: Post) => {
    return post.asset_urls?.assetType || ''
  })

  const getEmbedMedia = computed(() => (post: Post): EmbeddedMediaJSON => {
    return post.embedJson || ({} as EmbeddedMediaJSON)
  })

  const getPostVisibility = computed(() => (post: Post) => {
    return post.visibility || PostVisibility.published
  })

  const getContentType = computed(() => (post: Post) => {
    const contentType = post.contentType
    if (contentType === PostContentType.funnel) {
      return PostContentType.funnel
    }
    if (contentType === PostContentType.video && post.embedJson) {
      return PostContentType.embed
    }
    return contentType || PostContentType.video
  })

  const getIsContentAvailable = computed(() => (post: Post) => {
    return post.visibility === PostVisibility.published
  })

  const getIsQuiz = computed(() => (post: Post) => {
    return post.contentType === PostContentType.quiz
  })

  const getIsAssignment = computed(() => (post: Post) => {
    return post.contentType === PostContentType.assignment
  })

  const getIsVideo = computed(() => (post: Post) => {
    return (
      post.contentType === PostContentType.video &&
      !(post.embedJson || post.embedded_media) &&
      (post.video?.url || post.video?.assetsLicenseId)
    )
  })

  const getIsAudio = computed(() => (post: Post) => {
    return post.contentType === PostContentType.audio
  })

  const getIsEmbed = computed(() => (post: Post) => {
    return post.contentType === PostContentType.video && (post.embedJson || post.embedded_media)
  })

  const getIsFunnel = computed(() => (post: Post) => {
    return post.contentType === PostContentType.funnel
  })

  const getIsCertificate = computed(() => (post: Post) => {
    return post.certificateTemplateId
  })

  return {
    // Computed getters
    getPostMaterials,
    getAssetLicenseId,
    getAssetType,
    getEmbedMedia,
    getPostVisibility,
    getContentType,
    getIsContentAvailable,
    getIsQuiz,
    getIsAssignment,
    getIsVideo,
    getIsAudio,
    getIsEmbed,
    getIsFunnel,
    getIsCertificate,
    getVideoPlayedSoFar,
    getVideoCompletedPercentage,
    getAudioPlayedSoFar,
    getAudioCompletedPercentage,

    setVideoPlayedSoFar,
    setAudioPlayedSoFar,
  }
})
